import React, { FC, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  SideMenu as SideMenuEPIA,
  SideMenuProps,
  UserInfo,
} from '@itransition-epia/itransition-ui-components';
import { UserInfoProps } from '@itransition-epia/itransition-ui-components/lib/export/user-info';
import { externalSystemsPortals, useGetAppRoutes } from 'constants/menu-routes';
import { getViewDashboardLink } from 'constants/routes';
import { useEmployee, useImageBlobSrc } from 'hooks';
import { useRole } from 'hooks/use-role';
import { useSearch } from 'hooks/use-search';
import { allowSearchEmployees } from 'types/user-roles';
import styles from './styles.less';

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const skeletonOptions = [1, 2, 3].map((n) => ({
  label: (
    <UserInfo
      key={`loading-${n}`}
      username=""
      lastName=""
      firstName=""
      loading={true}
    />
  ),
  value: `loading-${n}`,
}));

const UserInfoWrapper: FC<UserInfoProps> = (props) => {
  const { src } = useImageBlobSrc(props.avatarImg || '');

  return <UserInfo {...props} avatarImg={src || undefined} />;
};

export const SideMenu: FC = () => {
  const { instance } = useMsal();
  const logout = useCallback(() => instance.logoutRedirect(), [instance]);
  const { employee } = useEmployee();
  const { data: roleData } = useRole();
  const showSearchBar = roleData && allowSearchEmployees(roleData.role.role);
  const appRoutes = useGetAppRoutes();

  const { employees, loading, search } = useSearch();

  const options = loading
    ? skeletonOptions
    : employees.map((ep) => ({
        label: (
          <Link to={getViewDashboardLink(ep.dashboardLink)}>
            <UserInfoWrapper
              firstName={ep.firstName || ''}
              lastName={ep.lastName || ''}
              username={ep.login || ''}
              avatarImg={ep.photo || undefined}
              qualification={ep.qualification || ''}
            />
          </Link>
        ),
        value: ep.login,
      }));

  const onSearch = (text: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (text.length < 2) {
      return;
    }

    void search(text);
  };

  const searchBarProps: SideMenuProps['searchBar'] = !showSearchBar
    ? undefined
    : {
        dropdownClass: styles['custom-options-dropdown'],
        onSearch,
        options,
        placeholderText: 'Search by name',
        throttleTimeout: 500,
        ...{ listHeight: 540 },
      };

  return (
    <SideMenuEPIA
      onUserLogout={logout}
      navigationRouteMap={appRoutes}
      portalName={'HOME'}
      portals={externalSystemsPortals}
      fullName={`${employee?.firstName || ''} ${employee?.lastName || ''}`}
      portalColor={'#BD1E88'}
      backgroundColor2={'#F2EFF1'}
      backgroundColor1={'#F0E7ED'}
      searchBar={searchBarProps}
    />
  );
};
